import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, MapPin, Users, FileText, Save, X, Bell, Bold, Italic, Underline, List, ListOrdered, Link as LinkIcon } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMembers } from '../hooks/useMembers.ts';
import api from '../utils/axios.ts';
import { toast } from 'react-hot-toast';

const CreateMeetingPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: membersData } = useMembers();
  const members = membersData?.data || [];
  const agendaEditorRef = useRef(null);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    meeting_date: '',
    start_time: '',
    location: '',
    type: 'regular',
    agenda: '',
    invite_all: true,
    invited_members: [],
    send_notifications: true,
  });

  const [errors, setErrors] = useState({});

  // Rich text editor functions
  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    agendaEditorRef.current?.focus();
  };

  const handleAgendaChange = () => {
    if (agendaEditorRef.current) {
      setFormData(prev => ({ ...prev, agenda: agendaEditorRef.current.innerHTML }));
    }
  };

  const createMeetingMutation = useMutation({
    mutationFn: async (data) => {
      const response = await api.post('/meetings', data);
      return response.data;
    },
    onSuccess: () => {
      toast.success('Meeting created successfully!');
      queryClient.invalidateQueries(['meetings']);
      navigate('/meetings');
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || 'Failed to create meeting';
      const validationErrors = error?.response?.data?.errors;
      
      if (validationErrors) {
        const firstError = Object.values(validationErrors)[0][0];
        toast.error(`Validation failed: ${firstError}`);
        setErrors(validationErrors);
      } else {
        toast.error(errorMessage);
      }
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    setErrors({});
    
    // Basic validation
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.meeting_date) newErrors.meeting_date = 'Date is required';
    if (!formData.start_time) newErrors.start_time = 'Time is required';
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error('Please fill in all required fields');
      return;
    }
    
    createMeetingMutation.mutate(formData);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/meetings')}
                className="p-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <ArrowLeft size={20} />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Create Meeting</h1>
                <p className="text-sm text-gray-600 dark:text-gray-400">Schedule a new meeting with your team</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => navigate('/meetings')}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={createMeetingMutation.isPending}
                className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {createMeetingMutation.isPending ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Creating...
                  </>
                ) : (
                  <>
                    <Save size={18} />
                    Create Meeting
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information Card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
              <FileText size={20} className="text-blue-600" />
              Basic Information
            </h2>
            
            <div className="space-y-6">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Meeting Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  placeholder="e.g., Monthly Team Meeting"
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                    errors.title ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.title && (
                  <p className="mt-1 text-sm text-red-500">{errors.title}</p>
                )}
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  placeholder="Brief description of the meeting purpose..."
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                />
              </div>

              {/* Date and Time */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    <Calendar size={16} className="inline mr-1" />
                    Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={formData.meeting_date}
                    onChange={(e) => handleChange('meeting_date', e.target.value)}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                      errors.meeting_date ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.meeting_date && (
                    <p className="mt-1 text-sm text-red-500">{errors.meeting_date}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    <Clock size={16} className="inline mr-1" />
                    Time <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="time"
                    value={formData.start_time}
                    onChange={(e) => handleChange('start_time', e.target.value)}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                      errors.start_time ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.start_time && (
                    <p className="mt-1 text-sm text-red-500">{errors.start_time}</p>
                  )}
                </div>
              </div>

              {/* Location */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  <MapPin size={16} className="inline mr-1" />
                  Location
                </label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => handleChange('location', e.target.value)}
                  placeholder="e.g., Conference Room A or Zoom link"
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                />
              </div>

              {/* Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  <Users size={16} className="inline mr-1" />
                  Meeting Type
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => handleChange('type', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value="regular">Regular Meeting</option>
                  <option value="emergency">Emergency Meeting</option>
                  <option value="agm">Annual General Meeting</option>
                  <option value="special">Special Meeting</option>
                </select>
              </div>

              {/* Member Invitations */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  <Users size={16} className="inline mr-1" />
                  Invite Members
                </label>
                
                {/* Toggle between All/Specific */}
                <div className="flex gap-4 mb-4">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      checked={formData.invite_all}
                      onChange={() => {
                        handleChange('invite_all', true);
                        handleChange('invited_members', []);
                      }}
                      className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                      All Members ({members.length})
                    </span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      checked={!formData.invite_all}
                      onChange={() => handleChange('invite_all', false)}
                      className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                      Specific Members
                    </span>
                  </label>
                </div>

                {/* Member Selection (only show if specific members selected) */}
                {!formData.invite_all && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400 mb-2">
                      <span>{formData.invited_members.length} member(s) selected</span>
                      {formData.invited_members.length > 0 && (
                        <button
                          type="button"
                          onClick={() => handleChange('invited_members', [])}
                          className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                        >
                          Clear all
                        </button>
                      )}
                    </div>
                    
                    <div className="max-h-60 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-lg p-3 space-y-2">
                      {members.length === 0 ? (
                        <p className="text-sm text-gray-500 text-center py-4">No members available</p>
                      ) : (
                        members.map((member) => (
                          <label
                            key={member.id}
                            className="flex items-center p-2 hover:bg-gray-50 dark:hover:bg-gray-700 rounded cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={formData.invited_members.includes(member.id)}
                              onChange={(e) => {
                                const newInvited = e.target.checked
                                  ? [...formData.invited_members, member.id]
                                  : formData.invited_members.filter(id => id !== member.id);
                                handleChange('invited_members', newInvited);
                              }}
                              className="w-4 h-4 text-blue-600 focus:ring-blue-500 rounded"
                            />
                            <div className="ml-3 flex-1">
                              <p className="text-sm font-medium text-gray-900 dark:text-white">
                                {member.name}
                              </p>
                              {member.phone_number && (
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  {member.phone_number}
                                </p>
                              )}
                            </div>
                          </label>
                        ))
                      )}
                    </div>

                    {/* Selected members chips */}
                    {formData.invited_members.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        {formData.invited_members.map((memberId) => {
                          const member = members.find(m => m.id === memberId);
                          if (!member) return null;
                          return (
                            <span
                              key={memberId}
                              className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-full text-sm"
                            >
                              {member.name}
                              <button
                                type="button"
                                onClick={() => {
                                  const newInvited = formData.invited_members.filter(id => id !== memberId);
                                  handleChange('invited_members', newInvited);
                                }}
                                className="hover:text-blue-900 dark:hover:text-blue-100"
                              >
                                <X size={14} />
                              </button>
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Notification Settings Card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Bell size={20} className="text-blue-600" />
              Notification Settings
            </h2>
            
            <div className="flex items-start gap-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="flex items-center h-6">
                <input
                  type="checkbox"
                  id="send_notifications"
                  checked={formData.send_notifications}
                  onChange={(e) => handleChange('send_notifications', e.target.checked)}
                  className="w-5 h-5 text-blue-600 focus:ring-blue-500 rounded"
                />
              </div>
              <div className="flex-1">
                <label htmlFor="send_notifications" className="block text-sm font-medium text-gray-900 dark:text-white cursor-pointer">
                  Send notifications to invited members
                </label>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {formData.invite_all 
                    ? `All members (${members.length}) will receive an SMS and in-app notification about this meeting.`
                    : formData.invited_members.length > 0
                      ? `${formData.invited_members.length} selected member(s) will receive an SMS and in-app notification about this meeting.`
                      : 'Select members to invite first.'
                  }
                </p>
                {formData.send_notifications && (
                  <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                    <p className="text-xs text-blue-800 dark:text-blue-200">
                      <strong>Notification will include:</strong>
                      <br />• Meeting title and description
                      <br />• Date, time, and location
                      <br />• Link to view meeting details
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Agenda Card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
              <FileText size={20} className="text-blue-600" />
              Meeting Agenda
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Create a detailed agenda for your meeting. Use the toolbar to format your content.
            </p>
            
            <div className="agenda-editor">
              {/* Toolbar */}
              <div className="flex flex-wrap gap-1 p-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-t-lg">
                <button
                  type="button"
                  onClick={() => execCommand('bold')}
                  className="p-2 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                  title="Bold"
                >
                  <Bold size={18} />
                </button>
                <button
                  type="button"
                  onClick={() => execCommand('italic')}
                  className="p-2 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                  title="Italic"
                >
                  <Italic size={18} />
                </button>
                <button
                  type="button"
                  onClick={() => execCommand('underline')}
                  className="p-2 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                  title="Underline"
                >
                  <Underline size={18} />
                </button>
                <div className="w-px bg-gray-300 dark:bg-gray-600 mx-1"></div>
                <button
                  type="button"
                  onClick={() => execCommand('insertUnorderedList')}
                  className="p-2 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                  title="Bullet List"
                >
                  <List size={18} />
                </button>
                <button
                  type="button"
                  onClick={() => execCommand('insertOrderedList')}
                  className="p-2 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                  title="Numbered List"
                >
                  <ListOrdered size={18} />
                </button>
                <div className="w-px bg-gray-300 dark:bg-gray-600 mx-1"></div>
                <button
                  type="button"
                  onClick={() => {
                    const url = prompt('Enter URL:');
                    if (url) execCommand('createLink', url);
                  }}
                  className="p-2 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                  title="Insert Link"
                >
                  <LinkIcon size={18} />
                </button>
                <select
                  onChange={(e) => execCommand('formatBlock', e.target.value)}
                  className="px-2 py-1 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded text-sm"
                  defaultValue=""
                >
                  <option value="">Normal</option>
                  <option value="h1">Heading 1</option>
                  <option value="h2">Heading 2</option>
                  <option value="h3">Heading 3</option>
                </select>
              </div>
              
              {/* Editor */}
              <div
                ref={agendaEditorRef}
                contentEditable
                onInput={handleAgendaChange}
                className="w-full min-h-[400px] px-4 py-3 border border-t-0 border-gray-300 dark:border-gray-600 rounded-b-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 dark:text-white"
                style={{ 
                  maxHeight: '500px', 
                  overflowY: 'auto',
                  lineHeight: '1.6'
                }}
                data-placeholder="Start typing your agenda here...

Example:
1. Call to Order
2. Review of Previous Minutes
3. Financial Report
4. New Business
   - Proposal A
   - Proposal B
5. Open Discussion
6. Adjournment"
              />
            </div>
          </div>

          {/* Preview Card */}
          {formData.agenda && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Agenda Preview
              </h2>
              <div 
                className="prose dark:prose-invert max-w-none"
                dangerouslySetInnerHTML={{ __html: formData.agenda }}
              />
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default CreateMeetingPage;
